import React, { useCallback, useEffect, useState } from "react";
import {
    Typography,
    List,
    ListItem,
    ListItemText,
    Box,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Paper,
} from "@mui/material";
import { format } from "date-fns";
import { CartType, hasMenuCartItem } from "../models/CartType";
import { OrderHistoryService, OrderViewData } from "../services/order/orderHistoryService";
import { useAuthRouting, useSignedInUserState } from "../hooks/useAuthState";
import { Timestamp } from "firebase/firestore";
import { StringsService } from "../services/strings/strings";
import CollectionCode from "./collectionCode";
import { useNavigate } from "react-router-dom";
import "./orderHistory.css";
import CenteredLoader from "../shared/loading/customLoading";
import Lottie from "lottie-react";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CartPillButton from "../listingDetailsPage/cartPillButton";
import { viewHightMinusHeader } from "../header";
import { formatMoney } from "../services/strings/formatters";
import { getPriceOfMenuCart } from "../models/fullMenuCart";
import { FullMenuPageData } from "../profilePage/maintainSpecificRestaurant/upsertFullMenuHelpers";
import { menuService } from "../services/firebase/menuService";
import { DateRangePill } from "../shared/dateRangePill";

const OrderHistoryPage: React.FC = () => {
    const [orders, setOrders] = useState<OrderViewData[]>([]);
    const [cartItemLabels, setCartItemLabels] = useState<{ [cartItemId: string]: string }>({})
    const [loading, setLoading] = useState<boolean>(true);
    const [showCartCode, setShowCartCode] = useState(false);
    const [cartCode, setCartCode] = useState("")
    const { profile } = useSignedInUserState();
    const navigate = useNavigate();
    useAuthRouting();
    const [animationData, setAnimationData] = useState<any>(null);
    const animationPath = "/animation/no_order_history_animation.json";
    const [menuData, setMenuDataState] = useState<Map<string, FullMenuPageData>>(
        () => new Map());
    // minimal setter: setMenuData(id, data)
    const setMenuData = useCallback((id: string, data: FullMenuPageData) => {
        setMenuDataState(prev => {
            const next = new Map(prev);
            next.set(id, data);
            return next;
        });
    }, []);

    useEffect(() => {
        fetch(animationPath)
            .then((response) => response.json())
            .then((data) => setAnimationData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [animationPath]);

    useEffect(() => {
        const load = async () => {
            // runtime guard: true if object has the menuCartItem key
            if (profile) {
                try {
                    const raw = await OrderHistoryService.getAllOrders(profile);
                    const orders = raw.filter(o => o.cart.items.length > 0 && hasMenuCartItem(o.cart.items[0]));
                    await Promise.all(orders.map(
                        async (o) => {
                            const menu = await menuService.fetchFullMenuCached(o.restaurant.id);
                            setMenuData(o.restaurant.id, menu);
                        }
                    ))
                    setOrders(orders);
                } catch (error) {
                    console.error("Error fetching order history:", error);
                    setOrders([]);
                } finally {
                    setLoading(false);
                }
            }
        };

        load();
    }, [profile]);

    const getStatusMessage = (status: CartType["status"]) => {
        switch (status) {
            case "PAYMENT_COMPLETE":
                return "Payment Complete";
            case "PAYMENT_PENDING":
                return "Payment Pending";
            case "RESTAURANT_ACCEPTED_ORDER":
                return "Waiting for the restaurant";
            case "PREPARING_ORDER":
                return "Preparing Order";
            case "READY_FOR_COLLECTION":
                return "Ready for Collection";
            case "COLLECTED":
                return "Collected";
            case "PARTIAL_REFUND_COMPLETE":
            case "FULL_REFUND_COMPLETE":
                return "Refund Complete";
            case "REFUND_REJECTED":
                return "Refund Rejected";
            case "FULL_REFUND_PROCESSING":
                return "Full Refund Processing";
            case "PARTIAL_REFUND_PROCESSING":
                return "Partial Refund Processing";
            case "PENDING_RESTAURANT":
                return "pending refund";
            case "FAILED":
                return "Failed";
            default:
                return "Unknown Status";
        }
    };


    const getStatusChipColor = (status: CartType["status"]) => {
        switch (status) {
            case "PAYMENT_COMPLETE":
                return "#A6C48A";
            case "RESTAURANT_ACCEPTED_ORDER":
                return "#A6C48A";
            case "PREPARING_ORDER":
                return "#A6C48A";
            case "READY_FOR_COLLECTION":
                return "#A6C48A";
            case "COLLECTED":
                return "#162C1F";
            case "PARTIAL_REFUND_COMPLETE":
                return "#162C1F";
            case "FULL_REFUND_COMPLETE":
                return "#162C1F";
            case "FULL_REFUND_PROCESSING":
                return "#FFA07A";
            case "PARTIAL_REFUND_PROCESSING":
                return "#FFA07A";
            case "PENDING_RESTAURANT":
                return "#A6C48A";
            case "FAILED":
                return "FF6F61";
            default:
                return "default";
        }
    };

    const formatTime = (t: string | Timestamp): string => {
        if (t) {
            if (t instanceof Timestamp) {
                return format(t.toDate(), "yyyy-MM-dd HH:mm");
            }
            const d = new Date(t);
            if (isNaN(d.getTime())) {
                return "";
            }
            return format(d, "yyyy-MM-dd HH:mm");
        }
        return "";
    };

    function formatDateFromString(timestamp: string): string {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    const handleShowOrderCode = (collectionNumber: number) => {
        setCartCode((collectionNumber).toString())
        setShowCartCode(true);
    }

    const orderWasRefunded = (order: OrderViewData): boolean => {
        if (order.cart.isRefundable) {
            return false
        }
        if (order.cart.refundRequestId && (
            order.cart.status == "FULL_REFUND_COMPLETE" ||
            order.cart.status == "FULL_REFUND_PROCESSING" ||
            order.cart.status == "PARTIAL_REFUND_COMPLETE" ||
            order.cart.status == "PARTIAL_REFUND_PROCESSING" ||
            order.cart.status == "REFUND_REJECTED" ||
            order.cart.status == "PENDING_RESTAURANT"
        )) {
            return true;
        }
        return false;
    }

    if (loading) {
        return (
            <Box
                className={"body"}
                sx={{
                    backgroundColor: "#F5F9E9",
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 2,
                }}
            >
                <CenteredLoader />
            </Box>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <Box
                className={"body"}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: "100vh",
                }}
            >
                <Typography textAlign={"center"} variant="h4" sx={{ marginTop: 2, marginBottom: 4 }}>
                    Order History
                </Typography>
                <Lottie animationData={animationData} style={{ width: 200, height: 200 }} />
                <Typography variant="h5" gutterBottom>
                    Nothing yet
                </Typography>
                <Typography variant="body1" color="textSecondary" textAlign={"center"} sx={{ maxWidth: 400 }}>
                    Every meal makes an impact. When you order, your journey to conscious eating begins! 🥗
                </Typography>
            </Box>
        );
    }


    return (
        <Box sx={{ padding: 3, minHeight: viewHightMinusHeader }} className="body">
            <Typography variant="h4" gutterBottom>
                Order History
            </Typography>
            <CartPillButton />
            <List>
                {orders.map((order: OrderViewData) => (
                    <React.Fragment key={order.cart.transactionId}>
                        <Paper
                            elevation={4}
                            sx={{
                                bgcolor: 'background.paper',
                                p: 2,
                                mb: 2,
                                borderRadius: 2,
                            }}
                        >
                            <ListItem
                                alignItems="flex-start"
                                disableGutters
                                sx={{
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'flex-start', sm: 'flex-start' },
                                }}
                            >
                                <ListItemText
                                    primary={`Restaurant: ${order.restaurant.name}`}
                                    secondary={
                                        <>
                                            <Typography variant="body2" color="textSecondary">
                                                Order Id: {order.cart.id}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Total: R{(order.cart.totalAmount / 100).toFixed(2)}
                                            </Typography>
                                            {order.cart.refundedAmount !== 0 && (
                                                <Typography variant="body2" color="textSecondary">
                                                    Total Refund: R{(order.cart.refundedAmount / 100).toFixed(2)}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" color="textSecondary">
                                                {order.cart.items.map(item =>
                                                    <Box
                                                        display="flex"
                                                        flexDirection={{ xs: 'column', sm: 'row' }}
                                                        justifyContent="space-between"
                                                        width="100%"
                                                        gap={2}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="h5" style={{ whiteSpace: 'pre-line' }}>
                                                                    {cartItemLabels[item.menuCartItem.id]}
                                                                </Typography>
                                                            }
                                                            secondary={<Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                                                                {/* Total */}
                                                                {menuData.get(order.restaurant.id) != undefined &&
                                                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                        <strong>Total:</strong>{' '}
                                                                        {formatMoney(getPriceOfMenuCart([item.menuCartItem], menuData.get(order.restaurant.id)!) / 100)}
                                                                    </Typography>
                                                                }

                                                                {/* Items */}
                                                                {item.menuCartItem.items.map((it, idx) => (
                                                                    <Box key={it.id ?? idx} sx={{ pl: 1, mb: 0.25 }}>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                            <strong>Sub item:</strong> {it.name}
                                                                        </Typography>

                                                                        {/* Selected variants as a simple bulleted list */}
                                                                        <List
                                                                            component="ul"
                                                                            sx={{
                                                                                listStyleType: 'disc',
                                                                                pl: 2,    // indent bullets
                                                                                mb: 0,
                                                                                mt: 0,
                                                                            }}
                                                                        >
                                                                            <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                                <strong>Customizations</strong>
                                                                            </Typography>
                                                                            {it.selectedVariants.map((selVar, vIdx) => {
                                                                                const price = !item.menuCartItem.comboVariant ? selVar.price : selVar.comboPrice;
                                                                                return (
                                                                                    <ListItem
                                                                                        key={selVar.id ?? vIdx}
                                                                                        component="li"
                                                                                        sx={{
                                                                                            display: 'list-item',
                                                                                            py: 0,    // remove vertical padding
                                                                                            my: 0,    // remove vertical margin
                                                                                        }}
                                                                                    >
                                                                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                                            <strong>Selected:</strong> {selVar.name}
                                                                                            {price > 0 ? ` (+ ${formatMoney(price / 100)})` : ''}
                                                                                        </Typography>
                                                                                    </ListItem>
                                                                                );
                                                                            })}
                                                                        </List>

                                                                        {/* Selected options (if any) */}
                                                                        {it.selectedOptions && it.selectedOptions.length > 0 && (
                                                                            <List component="ul" sx={{ listStyleType: 'disc', pl: 2, mb: 0, mt: 0 }}>
                                                                                <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                                    <strong>Extras</strong>
                                                                                </Typography>
                                                                                {it.selectedOptions.map((selOpt, oIdx) => (
                                                                                    <ListItem
                                                                                        key={selOpt.option?.id ?? oIdx}
                                                                                        component="li"
                                                                                        sx={{ display: 'list-item', py: 0, my: 0 }}
                                                                                    >
                                                                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                                            {selOpt.option.name} ( + {formatMoney(selOpt.option.price / 100)})
                                                                                        </Typography>
                                                                                    </ListItem>
                                                                                ))}
                                                                            </List>
                                                                        )}
                                                                    </Box>
                                                                ))}

                                                                {/* Category for the chosen combo variant (if present) */}
                                                                {item.menuCartItem.comboVariant && menuData.get(order.restaurant.id) && (() => {
                                                                    const foundCat = menuData.get(order.restaurant.id)!.menu.categories.find((cat) =>
                                                                        cat.items.some((ci) => ci.comboVariants?.some((cv) => cv.id === item.menuCartItem.comboVariant!.id))
                                                                    );
                                                                    return foundCat ? (
                                                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                            <strong>Category:</strong> {foundCat.name}
                                                                        </Typography>
                                                                    ) : null;
                                                                })()}

                                                                <DateRangePill startDate={new Date()} endDate={new Date(order.cart.collectBy)} />
                                                            </Box>

                                                            }
                                                        />
                                                    </Box>
                                                )}
                                            </Typography>
                                            {order.cart.createdAt &&
                                                <Typography variant="body2" color="textSecondary">
                                                    Placed on: {formatTime(order.cart.createdAt as any)}
                                                </Typography>
                                            }
                                            <Typography variant="body2" color="textSecondary">
                                                Collect by: {formatDateFromString(order.restaurant.closingTime as any)}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                sx={{ mt: 2, mr: 2, border: "1px solid #162C1F", borderRadius: "24px" }}
                                                onClick={() => {
                                                    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${order.restaurant?.location.lat},${order.restaurant?.location.lng}`;
                                                    window.open(mapsUrl, "_blank");
                                                }}
                                            >
                                                <LocationOnIcon sx={{ color: "#162C1F" }} />
                                                <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                                                    Get directions
                                                </Typography>
                                            </Button>
                                            {order.cart.orderCollectionNumber && !order.cart.usesCustomerSettlement && (
                                                <Button
                                                    variant="outlined"
                                                    sx={{ mt: 2, mr: 2, border: "1px solid #162C1F", borderRadius: "24px" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShowOrderCode(order.cart.orderCollectionNumber!);

                                                    }}
                                                >
                                                    <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                                                        Get Order Code
                                                    </Typography>
                                                </Button>
                                            )}
                                            {order.cart.usesCustomerSettlement && (
                                                <Button
                                                    variant="outlined"
                                                    sx={{ mt: 2, mr: 2, border: "1px solid #162C1F", borderRadius: "24px" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/order/collect/${order.cart.id}`)
                                                    }}
                                                >
                                                    <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                                                        Collect Order
                                                    </Typography>
                                                </Button>
                                            )}
                                            {order.cart.isRefundable && (
                                                <Button
                                                    variant="outlined"
                                                    sx={{ mt: 2, border: "1px solid #162C1F", borderRadius: "24px" }}
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/order/history/refund/${order.cart.id}`);
                                                    }}
                                                >
                                                    <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                                                        Request Refund
                                                    </Typography>
                                                </Button>
                                            )}
                                            {orderWasRefunded(order) && (
                                                <Button
                                                    variant="outlined"
                                                    sx={{ mt: 2, border: "1px solid #162C1F", borderRadius: "24px" }}
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/order/history/viewRefund/${order.cart.refundRequestId}`);
                                                    }}
                                                >
                                                    <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                                                        View Refund
                                                    </Typography>
                                                </Button>
                                            )}
                                        </>
                                    }
                                />
                                <Chip
                                    label={getStatusMessage(order.cart.status)}
                                    sx={{
                                        color: "white",
                                        backgroundColor: getStatusChipColor(order.cart.status),
                                        marginLeft: { xs: 0, sm: 2 },
                                        marginTop: { xs: 1, sm: 0 },
                                        minWidth: 180,
                                    }}
                                />
                            </ListItem>
                        </Paper>
                    </React.Fragment>
                ))}
            </List>

            <Dialog
                open={showCartCode}
                onClose={() => setShowCartCode(false)}
            >
                <DialogTitle>Order Collection Code</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <CollectionCode code={cartCode} />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCartCode(false)} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrderHistoryPage;
